/**
 * Local management-surface CHAT model — the chat-first universal layer
 * (Phase 1 · chat-first correction batch · item 2).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHY THIS EXISTS (owner-locked, this batch + memory
 *  project_avbp_chat_first_universal):
 * ─────────────────────────────────────────────────────────────────────────
 *  "Chat surface on EVERY management screen. AI→data, forms = fallback
 *  only." The Project Conversation (conversation.ts) is the gold standard
 *  but it is bound to `projects/{pid}` Firestore collections. Lead Inbox
 *  triage and Ancillary intake are NOT projects yet (a lead becomes a
 *  project only on Convert; an ancillary may be standalone). They still
 *  need the SAME chat-first behaviour now.
 *
 *  So this is the SAME conceptual model as conversation.ts — verbatim
 *  append-only messages + AI-derived proposals (FK to message) + the
 *  visibility split — reduced to a PURE, in-memory, Firebase-free core so
 *  it is reusable on the lead/ancillary surfaces and unit-testable by the
 *  Node runner (Sprint-3/4 discipline). It deliberately MIRRORS the
 *  conversation.ts contract (verbatim sacrosanct · derivative-with-FK ·
 *  chip-by-default) rather than inventing a second model — when the real
 *  Firestore lead/ancillary collections land, this is the exact shape
 *  they persist.
 * ─────────────────────────────────────────────────────────────────────────
 */

/** Mirrors conversation.ts AuthorRole (kept local so this stays pure). */
export type ChatAuthorRole = 'client' | 'staff' | 'admin' | 'system';

/**
 * A verbatim chat message. APPEND-ONLY by construction: there is no
 * edit/delete export (same rule as conversation.postMessage). `internal`
 * = team/admin-only (never client-visible) — identical semantics to the
 * conversation spine.
 */
export interface ChatMsg {
  id: string;
  authorName: string;
  authorRole: ChatAuthorRole;
  /** Raw text exactly as typed. */
  body: string;
  internal: boolean;
  /** ms timestamp (deterministic in tests — caller supplies). */
  ts: number;
}

/**
 * An AI-derived proposal extracted from a message. Carries the FK back to
 * the verbatim source (`sourceMsgId`) — derivative-with-FK contract,
 * exactly like conversation.Proposal. `asksUser` → render as a bordered
 * bubble (AI is asking); else an ambient chip (chip-by-default lock).
 */
export interface ChatProposal {
  id: string;
  sourceMsgId: string;
  /** What the AI thinks the human wants done (one line). */
  title: string;
  detail: string;
  /** Structured hint the surface can apply to its form (the fallback). */
  patch: Record<string, unknown>;
  asksUser: boolean;
  status: 'open' | 'applied' | 'dismissed';
}

export interface ChatState {
  messages: ChatMsg[];
  proposals: ChatProposal[];
}

export function emptyChat(): ChatState {
  return { messages: [], proposals: [] };
}

/** Deterministic id mint (caller supplies a monotone seq — no random). */
export function chatMsgId(seq: number): string {
  return `cm-${String(seq).padStart(4, '0')}`;
}
export function chatPropId(seq: number): string {
  return `cp-${String(seq).padStart(4, '0')}`;
}

/**
 * THE extraction seam. Deterministic keyword mock (same spirit as
 * lib/extractor.ts) — the REAL LLM extractor drops in HERE with zero
 * surface change. It turns a verbatim message into 0+ proposals that, on
 * apply, patch the surface's fallback form. Pure: caller supplies the id
 * minter so it stays deterministic/testable.
 *
 * The patterns are intentionally aligned to the lead/ancillary fields the
 * fallback forms carry, so "chat first, form is the fallback" is real:
 * typing a sentence proposes the same field writes the form would take.
 */
export function extractChatProposals(
  msg: ChatMsg,
  mintId: (n: number) => string,
  startSeq: number,
): ChatProposal[] {
  const out: ChatProposal[] = [];
  const b = msg.body.toLowerCase();
  let seq = startSeq;
  const push = (p: Omit<ChatProposal, 'id' | 'sourceMsgId' | 'status'>) => {
    out.push({
      id: mintId(seq++),
      sourceMsgId: msg.id,
      status: 'open',
      ...p,
    });
  };

  // sqft → numeric patch
  const sqftM = b.match(/(\d[\d,]{2,})\s*(?:sq\s?ft|sqft|square\s?feet)/);
  if (sqftM) {
    const n = Number(sqftM[1].replace(/,/g, ''));
    push({
      title: `Set area to ${n.toLocaleString('en-IN')} sqft`,
      detail: `Heard a built-up area of ${n.toLocaleString('en-IN')} sqft.`,
      patch: { sqft: n },
      asksUser: false,
    });
  }

  // property type
  for (const t of ['residence', 'commercial', 'office', 'plot', 'retail', 'industrial']) {
    if (b.includes(t)) {
      push({
        title: `Property type: ${t}`,
        detail: `Mentioned a ${t}.`,
        patch: { propertyType: t },
        asksUser: false,
      });
      break;
    }
  }

  // tier mention (L1A..L3B)
  const tierM = msg.body.match(/\bL[123][ABCD]\b/);
  if (tierM) {
    push({
      title: `Lean toward tier ${tierM[0]}`,
      detail: `Service level ${tierM[0]} was named.`,
      patch: { desiredTier: tierM[0] },
      asksUser: false,
    });
  }

  // qualify / accept intent
  if (/\b(qualif|good fit|accept|real opportunity)\b/.test(b)) {
    push({
      title: 'Move lead to Qualified',
      detail: 'Reads like an accept/qualify decision.',
      patch: { action: 'qualify' },
      asksUser: true, // a state change → ask, render as a bubble
    });
  }

  // book consult
  if (/\b(book|schedule|appointment|consult)\b/.test(b)) {
    push({
      title: 'Book a consult',
      detail: 'Sounds like a booking should be recorded.',
      patch: { action: 'book_consult' },
      asksUser: true,
    });
  }

  // ancillary site visit cue
  if (/\bsite visit|visit the site|come over|outstation\b/.test(b)) {
    push({
      title: 'Raise a Site Visit',
      detail: 'A site visit was discussed — prefill the Site Visit form.',
      patch: { ancillary: 'site_visit' },
      asksUser: false,
    });
  }

  // concern free-text fallback — always capture the message as the concern
  // if nothing structured fired (so the verbatim is never lost to the form)
  if (out.length === 0 && msg.body.trim().length > 0 && !msg.internal) {
    push({
      title: 'Capture as concern / note',
      detail: 'Free-text — saved verbatim; nothing structured detected.',
      patch: { concernAppend: msg.body.trim() },
      asksUser: false,
    });
  }

  return out;
}

/**
 * Append a verbatim message + run extraction. Returns a NEW state (never
 * mutates — same immutability discipline as the leads/ledger reducers).
 * `seq` seeds the deterministic id mint for both the message and any
 * proposals it spawns.
 */
export function postChat(
  state: ChatState,
  input: {
    authorName: string;
    authorRole: ChatAuthorRole;
    body: string;
    internal?: boolean;
    ts: number;
  },
  seq: number,
): { state: ChatState; messageId: string } {
  const body = input.body.trim();
  if (!body) return { state, messageId: '' };
  const msg: ChatMsg = {
    id: chatMsgId(seq),
    authorName: input.authorName,
    authorRole: input.authorRole,
    body,
    internal: input.internal === true,
    ts: input.ts,
  };
  // Proposals get their own id space offset from the message seq so ids
  // never collide across a session.
  const props = input.internal
    ? [] // internal notes don't drive the AI→data layer
    : extractChatProposals(msg, chatPropId, seq * 10);
  return {
    state: {
      messages: [...state.messages, msg],
      proposals: [...state.proposals, ...props],
    },
    messageId: msg.id,
  };
}

/** Mark a proposal applied (the surface applied its patch to the form). */
export function applyProposal(state: ChatState, id: string): ChatState {
  return {
    ...state,
    proposals: state.proposals.map((p) =>
      p.id === id ? { ...p, status: 'applied' } : p,
    ),
  };
}

/** Dismiss a proposal — nothing else is written (reject discipline). */
export function dismissProposal(state: ChatState, id: string): ChatState {
  return {
    ...state,
    proposals: state.proposals.map((p) =>
      p.id === id ? { ...p, status: 'dismissed' } : p,
    ),
  };
}

/**
 * THE SPINE (mirrors conversation.visibleThread): team/admin see every
 * message; a client-track viewer never sees `internal` messages. Kept
 * here so the chat-first lead/ancillary surfaces enforce the SAME split
 * the Project Conversation enforces — one rule, applied everywhere.
 */
export function visibleChat(
  msgs: ChatMsg[],
  opts: { isTeamView: boolean },
): ChatMsg[] {
  if (opts.isTeamView) return msgs;
  return msgs.filter((m) => !m.internal);
}

/** Open (still-actionable) proposals — what the AI→data strip shows. */
export function openProposals(state: ChatState): ChatProposal[] {
  return state.proposals.filter((p) => p.status === 'open');
}

/* ════════════════════ AD-005 · client delivery is a conscious act ═══════
 *
 * OWNER RULING (2026-06-17, AVBP_ARCHITECTURE_DECISIONS_LOG AD-005): chat is
 * INTERNAL-BY-DEFAULT. Every message typed in chat is team-only (`internal:
 * true`) by default; it reaches a client ONLY via a deliberate act —
 * "Send to client" at compose, or "Forward to client" on an already-posted
 * message. There is NO accidental path to the client. The old "Internal
 * note" tick-box (an unsafe opt-IN-to-internal default) is removed.
 *
 * We keep ONE concept — the existing `internal` boolean + `visibleChat`
 * split — and merely INVERT the default at the surface. `internal === true`
 * still means team-only (never client-visible); `internal === false` means
 * the message has been consciously delivered to the client. `visibleChat`
 * is unchanged: a client sees only `internal === false` messages.
 * ──────────────────────────────────────────────────────────────────── */

/**
 * Promote an already-posted internal message to client-visible (the
 * "Forward to client" act). Immutable; flips `internal` false on the one
 * message. Append-only verbatim is preserved — the body is never edited,
 * only its visibility is consciously changed. No-op if the message is
 * already client-visible or not found.
 */
export function promoteToClient(state: ChatState, msgId: string): ChatState {
  return {
    ...state,
    messages: state.messages.map((m) =>
      m.id === msgId && m.internal ? { ...m, internal: false } : m,
    ),
  };
}

/**
 * Background audit of a conscious client-send (AD-005, step 7). Records who
 * sent what to the client and when. No UI friction — fire-and-forget.
 *
 * HARD CONSTRAINT (Module 1): this sets visibility + logs the audit ONLY. It
 * does NOT trigger any live outbound channel delivery (WhatsApp / email stay
 * OFF). The real channel-delivery hook lands in Module 3/8 — see the marked
 * TODO below. `sink` is injectable so this is unit-testable without console.
 */
export interface ClientSendAudit {
  actor: string;
  messageId: string;
  body: string;
  /** 'compose' = sent straight to client at compose; 'forward' = promoted later. */
  via: 'compose' | 'forward';
  ts: number;
}
export function logClientSend(
  audit: ClientSendAudit,
  sink?: (a: ClientSendAudit) => void,
): void {
  // ── TODO (Module 3/8) — CHANNEL DELIVERY HOOK ─────────────────────────
  // When the outbound channel is wired, dispatch the actual WhatsApp/email
  // HERE (gated, owner-approved). Module 1 deliberately does NOT send: this
  // only flips visibility + writes the audit line below. Live sends OFF.
  // ──────────────────────────────────────────────────────────────────────
  if (sink) {
    sink(audit);
    return;
  }
  try {
    // Background-only console audit in Module 1 (no UI surface).
    console.info(
      `[AD-005 client-send] actor=${audit.actor} via=${audit.via} msg=${audit.messageId} @${new Date(audit.ts).toISOString()}`,
    );
  } catch {
    /* never throw from an audit */
  }
}

/* ════════════════════ REAL Claude extraction (Round 2) ════════════════════
 *
 * OWNER (Round 2, 2026-05-18): replace the deterministic keyword stub with
 * a REAL Anthropic call. The stub above (`extractChatProposals`) is kept
 * VERBATIM as the automatic OFFLINE fallback — used only when the Cloud
 * Function is unreachable (network down, $0 Anthropic balance, emulator
 * not running). Real Claude is the default path.
 *
 * The Cloud Function (`avbpChatExtract`, firebase-functions) returns the
 * SAME { title, detail, patch, asksUser } shape this stub produces, so
 * the apply-proposal→patch-form flow downstream is unchanged. This is the
 * single typed seam the Sprint-2 extractor design always pointed at — the
 * stub is now the fallback, not the primary path.
 * ──────────────────────────────────────────────────────────────────── */

/** Surface the chat sits on — light context for the extractor. */
export type ChatSurfaceKind = 'lead' | 'ancillary';

/** Shape the CF returns (mirrors the server `__pure` contract). */
interface RemoteProposal {
  title: string;
  detail: string;
  patch: Record<string, unknown>;
  asksUser: boolean;
}

/**
 * Call the real Claude extractor. Returns proposals in the SAME on-state
 * shape as the stub. On ANY failure (CF unreachable, billing/precondition,
 * unexpected payload) it transparently falls back to the deterministic
 * keyword stub and logs clearly — the chat surface never breaks.
 *
 * `callCF` is injected so this is unit-testable without Firebase; the
 * default wires the real httpsCallable. Pure-ish: caller supplies the id
 * minter + seq exactly like the stub, so ids stay deterministic.
 */
export async function extractChatProposalsRemote(
  msg: ChatMsg,
  mintId: (n: number) => string,
  startSeq: number,
  ctx: { surface: ChatSurfaceKind; formState?: Record<string, unknown> },
  callCF?: (payload: {
    message: string;
    surface: ChatSurfaceKind;
    formState?: Record<string, unknown>;
  }) => Promise<{ proposals: RemoteProposal[] }>,
): Promise<{ proposals: ChatProposal[]; usedFallback: boolean }> {
  // Internal notes never drive the AI→data layer (same rule as the stub).
  if (msg.internal || !msg.body.trim()) {
    return { proposals: [], usedFallback: false };
  }

  const offline = (): { proposals: ChatProposal[]; usedFallback: boolean } => ({
    proposals: extractChatProposals(msg, mintId, startSeq),
    usedFallback: true,
  });

  let invoke = callCF;
  if (!invoke) {
    try {
      // Lazy import keeps this module Firebase-free for the Node test
      // runner (it only loads firebase when actually calling the CF).
      const [{ functions }, { httpsCallable }] = await Promise.all([
        import('./firebase'),
        import('firebase/functions'),
      ]);
      const fn = httpsCallable<
        { message: string; surface: ChatSurfaceKind; formState?: Record<string, unknown> },
        { proposals: RemoteProposal[] }
      >(functions, 'avbpChatExtract');
      invoke = async (payload) => (await fn(payload)).data;
    } catch (e) {
      console.warn(
        '[localChat] Claude CF client unavailable — using offline keyword stub.',
        e,
      );
      return offline();
    }
  }

  let data: { proposals: RemoteProposal[] };
  try {
    data = await invoke({
      message: msg.body.trim(),
      surface: ctx.surface,
      formState: ctx.formState,
    });
  } catch (e) {
    console.warn(
      '[localChat] Claude CF unreachable/billing — using offline keyword stub.',
      e,
    );
    return offline();
  }

  const list = Array.isArray(data?.proposals) ? data.proposals : [];
  // Mint deterministic ids exactly as the stub does (seq*10 offset is
  // applied by postChat; here we mirror extractChatProposals' contract).
  let seq = startSeq;
  const proposals: ChatProposal[] = list
    .filter((p) => p && typeof p === 'object' && p.patch && Object.keys(p.patch).length > 0)
    .map((p) => ({
      id: mintId(seq++),
      sourceMsgId: msg.id,
      status: 'open' as const,
      title: typeof p.title === 'string' && p.title.trim() ? p.title.trim() : 'Proposed update',
      detail: typeof p.detail === 'string' ? p.detail : '',
      patch: p.patch,
      asksUser: p.asksUser === true,
    }));

  return { proposals, usedFallback: false };
}

/* ════════════════════ CONVERSATIONAL AGENT (Round 3 · 2026-05-18) ═══════
 *
 * OWNER DIRECTION CHANGE (memory project_avbp_conversational_agent): the
 * AVBP AI is ONE conversational agent — the SAME brain in-app and on
 * WhatsApp. Instead of message → fixed chips, the user chats and the agent
 * REPLIES IN NATURAL LANGUAGE (and silently takes real actions server-side
 * via the avbpAgentChat Cloud Function: low-risk now / money → durable
 * owner queue). This is the PRIMARY in-app path; the keyword/extractor
 * chip flow above is kept as the offline/legacy fallback.
 *
 * The agent's reply is appended as a normal assistant chat message (role
 * 'staff' display name 'AV Assistant', NOT internal) so it renders as an
 * ordinary bubble in the shared thread — honouring the conversation
 * visibility split (the reply addressed to the user is visible; the
 * server keeps the internal tool-trace team-only, never sent to the
 * client app). No proposals/chips are produced on this path.
 * ──────────────────────────────────────────────────────────────────── */

/** What the avbpAgentChat callable returns (server `__pure` contract). */
interface AgentChatResponse {
  reply: string;
  actions: Array<Record<string, unknown>>;
  model: string;
  turns: number;
  cached: boolean;
  fallback: boolean;
  /** Echoed resolved screen scope (global screen-contextual chat). */
  screen?: string;
  /** Best-effort deep-link the UI may navigate to (agent-emitted). */
  navigateTo?: string | null;
}

/**
 * Screen context for the GLOBAL screen-contextual chat dock (2026-05-18b).
 * The dock derives these from the current route so the ONE agent answers /
 * acts about wherever the user is — Home, Lead Inbox, a project, etc.
 */
export interface ScreenCtx {
  /** coarse scope key: home|leads|clients|project|ancillary|… */
  screen: string;
  /** human label shown in the dock header AND given to the agent. */
  screenLabel: string;
  /** the relevant entity id for this screen (e.g. the project id). */
  screenEntityId?: string;
}

/**
 * Post a verbatim user message and append the agent's NATURAL reply.
 * Immutable-reducer discipline (same as postChat). `callCF` is injected so
 * this is unit-testable without Firebase; the default wires the real
 * httpsCallable to `avbpAgentChat`. On ANY failure the user message is
 * still preserved (verbatim-sacrosanct) and a graceful natural line is
 * appended — the surface never breaks.
 */
export async function postChatAgent(
  state: ChatState,
  input: {
    authorName: string;
    authorRole: ChatAuthorRole;
    body: string;
    internal?: boolean;
    ts: number;
  },
  seq: number,
  ctx: {
    projectId?: string;
    threadId?: string;
    /** GLOBAL screen-contextual chat: where the user is right now. */
    screen?: string;
    screenLabel?: string;
    screenEntityId?: string;
  },
  callCF?: (payload: {
    message: string;
    projectId?: string;
    threadId?: string;
    screen?: string;
    screenLabel?: string;
    screenEntityId?: string;
    history: Array<{ role: string; body: string }>;
  }) => Promise<AgentChatResponse>,
): Promise<{
  state: ChatState;
  messageId: string;
  fallback: boolean;
  navigateTo?: string | null;
}> {
  const body = input.body.trim();
  if (!body) return { state, messageId: '', fallback: false };

  // 1. Verbatim user message — appended FIRST, never lost even if the
  //    agent call fails (feedback_client_verbatim_preservation).
  const userMsg: ChatMsg = {
    id: chatMsgId(seq),
    authorName: input.authorName,
    authorRole: input.authorRole,
    body,
    internal: input.internal === true,
    ts: input.ts,
  };
  let next: ChatState = {
    messages: [...state.messages, userMsg],
    proposals: state.proposals,
  };

  // Internal notes are not a conversation with the agent.
  if (userMsg.internal) {
    return {
      state: next,
      messageId: userMsg.id,
      fallback: false,
      navigateTo: null,
    };
  }

  // 2. History = the prior visible turns (oldest→newest), verbatim.
  const history = state.messages
    .filter((m) => !m.internal)
    .map((m) => ({
      role:
        m.authorName === 'AV Assistant' ? 'agent' : m.authorRole,
      body: m.body,
    }));

  let invoke = callCF;
  if (!invoke) {
    try {
      const [{ functions }, { httpsCallable }] = await Promise.all([
        import('./firebase'),
        import('firebase/functions'),
      ]);
      const fn = httpsCallable<
        {
          message: string;
          projectId?: string;
          threadId?: string;
          screen?: string;
          screenLabel?: string;
          screenEntityId?: string;
          history: Array<{ role: string; body: string }>;
        },
        AgentChatResponse
      >(functions, 'avbpAgentChat');
      invoke = async (payload) => (await fn(payload)).data;
    } catch (e) {
      console.warn('[localChat] agent CF unavailable.', e);
      invoke = undefined;
    }
  }

  let reply =
    "I couldn't reach the assistant just now — your message is saved; please try again or contact the team.";
  let fallback = true;
  let navigateTo: string | null = null;
  if (invoke) {
    try {
      const res = await invoke({
        message: body,
        projectId: ctx.projectId,
        threadId: ctx.threadId,
        screen: ctx.screen,
        screenLabel: ctx.screenLabel,
        screenEntityId: ctx.screenEntityId,
        history,
      });
      if (res && typeof res.reply === 'string' && res.reply.trim()) {
        reply = res.reply.trim();
        fallback = res.fallback === true;
        navigateTo =
          typeof res.navigateTo === 'string' ? res.navigateTo : null;
      }
    } catch (e) {
      console.warn('[localChat] agent CF call failed.', e);
    }
  }

  // 3. Agent's natural reply as an ordinary assistant bubble (visible).
  const agentMsg: ChatMsg = {
    id: chatMsgId(seq + 1),
    authorName: 'AV Assistant',
    authorRole: 'staff',
    body: reply,
    internal: false,
    ts: input.ts + 1,
  };
  next = {
    messages: [...next.messages, agentMsg],
    proposals: next.proposals,
  };
  return { state: next, messageId: userMsg.id, fallback, navigateTo };
}

/* ════════════════════ IN-APP VOICE → TRANSCRIPT (2026-05-18b) ═══════════
 *
 * The portal chat has a mic but in-app voice never transcribed (only
 * WhatsApp did). This seam uploads the recorded audio blob to the
 * `avbpVoiceIngest` callable, which runs the SAME locked Whisper path
 * WhatsApp uses (functions lib/waMedia.transcribeVoice) and returns the
 * transcript. The caller then feeds that transcript to `postChatAgent`
 * exactly like a typed message — the raw audio is preserved verbatim
 * (the blob is the canonical input; transcript is the derivative), the
 * same contract as the WhatsApp voice note.
 *
 * `callCF` is injected so this is unit-testable without Firebase. On any
 * failure it returns a clear { ok:false } — the surface never breaks and
 * the user can retry or type instead.
 * ──────────────────────────────────────────────────────────────────── */

export interface VoiceTranscript {
  ok: boolean;
  transcript?: string;
  /** clean expected skip (e.g. no OpenAI key) vs a real error. */
  skipped?: boolean;
  reason?: string;
}

/**
 * Read a Blob → base64 data-URL. Browser-only (FileReader); injectable in
 * tests via `toDataUrl`.
 */
async function blobToDataUrl(blob: Blob): Promise<string> {
  return await new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = () => reject(fr.error || new Error('read failed'));
    fr.onload = () => resolve(String(fr.result || ''));
    fr.readAsDataURL(blob);
  });
}

/**
 * Upload a recorded audio blob and get its transcript via the shared
 * Whisper path. Pure-ish: `callCF` + `toDataUrl` are injectable so the
 * upload→transcript path is fully unit-testable without Firebase/DOM.
 */
export async function transcribeInAppVoice(
  blob: Blob,
  opts?: {
    mimeType?: string;
    fileName?: string;
    callCF?: (payload: {
      audioBase64: string;
      mimeType?: string;
      fileName?: string;
    }) => Promise<VoiceTranscript>;
    toDataUrl?: (b: Blob) => Promise<string>;
  },
): Promise<VoiceTranscript> {
  if (!blob || (typeof blob.size === 'number' && blob.size === 0)) {
    return { ok: false, reason: 'no_audio' };
  }
  const toUrl = opts?.toDataUrl || blobToDataUrl;
  let dataUrl: string;
  try {
    dataUrl = await toUrl(blob);
  } catch (e) {
    console.warn('[localChat] could not read recorded audio.', e);
    return { ok: false, reason: 'read_failed' };
  }

  let invoke = opts?.callCF;
  if (!invoke) {
    try {
      const [{ functions }, { httpsCallable }] = await Promise.all([
        import('./firebase'),
        import('firebase/functions'),
      ]);
      const fn = httpsCallable<
        { audioBase64: string; mimeType?: string; fileName?: string },
        VoiceTranscript
      >(functions, 'avbpVoiceIngest');
      invoke = async (p) => (await fn(p)).data;
    } catch (e) {
      console.warn('[localChat] voice CF unavailable.', e);
      return { ok: false, reason: 'cf_unavailable' };
    }
  }

  try {
    const res = await invoke({
      audioBase64: dataUrl,
      mimeType: opts?.mimeType || blob.type || 'audio/webm',
      fileName: opts?.fileName || 'inapp_voice.webm',
    });
    if (res && res.ok && typeof res.transcript === 'string') {
      return { ok: true, transcript: res.transcript.trim() };
    }
    return {
      ok: false,
      skipped: res?.skipped === true,
      reason: res?.reason || 'whisper_error',
    };
  } catch (e) {
    console.warn('[localChat] voice transcription call failed.', e);
    return { ok: false, reason: 'call_failed' };
  }
}

/**
 * Async sibling of `postChat` that uses the REAL Claude extractor (with
 * automatic offline fallback). Same immutable-reducer discipline + same
 * deterministic id seeding as `postChat` — only the extractor differs.
 */
export async function postChatRemote(
  state: ChatState,
  input: {
    authorName: string;
    authorRole: ChatAuthorRole;
    body: string;
    internal?: boolean;
    ts: number;
  },
  seq: number,
  ctx: { surface: ChatSurfaceKind; formState?: Record<string, unknown> },
  callCF?: Parameters<typeof extractChatProposalsRemote>[4],
): Promise<{ state: ChatState; messageId: string; usedFallback: boolean }> {
  const body = input.body.trim();
  if (!body) return { state, messageId: '', usedFallback: false };
  const msg: ChatMsg = {
    id: chatMsgId(seq),
    authorName: input.authorName,
    authorRole: input.authorRole,
    body,
    internal: input.internal === true,
    ts: input.ts,
  };
  const { proposals, usedFallback } = input.internal
    ? { proposals: [] as ChatProposal[], usedFallback: false }
    : await extractChatProposalsRemote(msg, chatPropId, seq * 10, ctx, callCF);
  return {
    state: {
      messages: [...state.messages, msg],
      proposals: [...state.proposals, ...proposals],
    },
    messageId: msg.id,
    usedFallback,
  };
}

/* ════════════════ Chat-patch → LEAD translator (parity GAP 4, 2026-07-03) ════════════════
 *
 *  The ONE translation from an applied chat proposal's `patch` onto a Lead.
 *  The parity audit caught the full-page record (`/leads/:id`) applying
 *  proposals as a raw spread (`{ ...lead, ...patch }`) — an `action:
 *  'qualify'` proposal wrote a junk `action` field instead of performing the
 *  transition. This PURE translator gives every host the drawer's semantics:
 *
 *    · sqft / propertyType / concernAppend — captured onto the record
 *      (the drawer stages these into its review-first form instead; hosts
 *      without a staging form apply them here directly);
 *    · desiredTier — writes the ASSIGNED tier (Option A confirm-loop: the
 *      record is the truth the Set-price panel reads), mirroring the drawer;
 *    · action 'qualify' — a REAL `transitionLead` hop (guarded; an illegal
 *      hop returns unchanged with the reason);
 *    · action 'book_consult' — the spine's `recordConsultBooked` event seam
 *      (legal multi-hop walk + date stamp);
 *    · anything else (e.g. `ancillary`) — NO write (never a junk field).
 *
 *  Pure + total: returns the next lead, whether anything changed, and a
 *  plain-language note for the host's message line.
 */
import {
  canTransition,
  recordConsultBooked,
  transitionLead,
  type Lead,
} from './leads';
import type { Tier } from './engagementTemplate';

export interface LeadChatPatchResult {
  /** The lead AFTER the patch (=== input lead when nothing applied). */
  lead: Lead;
  /** true → the caller should persist `lead`. */
  changed: boolean;
  /** Plain-language outcome for the host's message line. */
  note: string;
}

export function applyLeadChatPatch(
  lead: Lead,
  patch: Record<string, unknown>,
  nowISO: string,
): LeadChatPatchResult {
  let next: Lead = lead;
  const notes: string[] = [];

  if (typeof patch.sqft === 'number' && Number.isFinite(patch.sqft)) {
    next = { ...next, sqft: Math.round(patch.sqft), updatedAt: nowISO };
    notes.push(`area set to ${Math.round(patch.sqft)} sqft`);
  }
  if (typeof patch.propertyType === 'string' && patch.propertyType.trim()) {
    next = { ...next, propertyType: patch.propertyType.trim(), updatedAt: nowISO };
    notes.push(`property type "${patch.propertyType.trim()}"`);
  }
  if (typeof patch.concernAppend === 'string' && patch.concernAppend.trim()) {
    const add = patch.concernAppend.trim();
    next = {
      ...next,
      concern: next.concern ? `${next.concern}\n${add}` : add,
      updatedAt: nowISO,
    };
    notes.push('captured into the concern');
  }
  if (typeof patch.desiredTier === 'string' && patch.desiredTier.trim()) {
    // Option A confirm-loop (mirrors the drawer): applying the proposal
    // writes the assigned tier onto the RECORD — the truth Set-price reads.
    const t = patch.desiredTier as Tier;
    next = {
      ...next,
      assignedTier: t,
      desiredTier: next.desiredTier ?? t,
      updatedAt: nowISO,
    };
    notes.push(`service ${t} set on the record`);
  }
  if (patch.action === 'qualify') {
    if (canTransition(next.state, 'qualified')) {
      next = transitionLead(next, 'qualified', nowISO);
      notes.push('lead moved to Qualified');
    } else {
      notes.push(
        next.state === 'qualified'
          ? 'already Qualified'
          : `cannot move to Qualified from "${next.state}"`,
      );
    }
  }
  if (patch.action === 'book_consult') {
    const before = next;
    next = recordConsultBooked(next, nowISO, nowISO);
    notes.push(
      next.state !== before.state
        ? 'consult booked — stage moved to Consult booked'
        : 'consult date recorded',
    );
  }

  const changed = next !== lead;
  return {
    lead: next,
    changed,
    note: notes.length
      ? `Chat → ${notes.join(' · ')}.`
      : 'Nothing to apply from that proposal.',
  };
}
